import {
    Alert,
    Button,
    Col,
    Input,
    Row,
    Space,
    Table,
    Typography,
} from 'antd';
import cs from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoSearch } from 'react-icons/io5';
import { getDrivers } from '../../../../api/drivers.api';
import { IDriver } from '../../../../interfaces/driver.interface';
import { IPagination } from '../../../../interfaces/paginate.interface';
import { IRequest } from '../../../../interfaces/requests.interface';
import { useWorkspaceStore } from '../../../../store/workspace.store';
import { checkDriverStatus } from '../../../../utils/driver.util';

interface Props {
    request: IRequest | null;
    handleAssignDriver: (driverID: string, bookingID: string, orderID: string) => Promise<boolean>;
}

const { Text } = Typography;

const isDriverAssignable = (driver: IDriver): boolean => {
    const isConnected = driver.onDutyFlag;
    const activeBookingsCount = driver.bookings?.length ?? 0;
    const hasFewerThanTwoBookings = activeBookingsCount <= 2;
    return isConnected && hasFewerThanTwoBookings;
};

const AvailableDrivers: FC<Props> = ({ request, handleAssignDriver }) => {
    const { t } = useTranslation('drivers', { keyPrefix: 'DRIVERS' });

    const workspace = useWorkspaceStore((state) => state.data);

    const PAGE = 1;
    const LIMIT = 10;

    const [availableDrivers, setAvailableDrivers] = useState<IDriver[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [assigningId, setAssigningId] = useState<string | null>(null);
    const [search, setSearch] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [pagination, setPagination] = useState<IPagination>({
        limit: LIMIT,
        page: PAGE,
        total: 0,
        totalPages: 0,
    });

    const onAssign = (driver: IDriver) => {
        setAssigningId(driver._id);
        handleAssignDriver(driver._id, request?.bookingID as string, request?._id as string)
            .finally(() => setAssigningId(null));
    };

    const columns = [
        {
            title: t('FULL_NAME'),
            dataIndex: 'name',
            key: 'name',
            align: 'left' as 'left',
            onHeaderCell: () => ({ style: { paddingLeft: 12 } }),
            onCell: () => ({ style: { paddingLeft: 12 } }),
            render: (name: string, driver: IDriver) => (
                <Space direction="vertical" size={0}>
                    <Text>{`${name} ${driver.lastName}`}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{driver.email}</Text>
                </Space>
            ),
        },
        {
            title: t('STATUS'),
            dataIndex: 'status',
            key: 'status',
            align: 'center' as 'center',
            render: (_: string, driver: any) => {
                const { isBusy, onDutyFlag } = driver;
                const status = checkDriverStatus(isBusy, onDutyFlag);
                return (
                    <div className={cs('table-status', status)}>
                        {t(status)}
                    </div>
                );
            },
        },
        {
            title: t('DISTANCE'),
            dataIndex: 'distance',
            key: 'distance',
            align: 'center' as 'center',
            render: (distance: number) => (
                <Text>
                    {`${(distance ?? 0).toFixed(2)} km`}
                </Text>
            ),
        },
        {
            title: t('ACTIONS'),
            dataIndex: 'actions',
            key: 'actions',
            align: 'center' as 'center',
            onHeaderCell: () => ({ style: { paddingRight: 12 } }),
            onCell: () => ({ style: { paddingRight: 12 } }),
            render: (actions: string, driver: IDriver) => {
                const isAssigning = assigningId === driver._id;
                return (
                    <Button
                        type="primary"
                        onClick={() => onAssign(driver)}
                        size="small"
                        loading={isAssigning}
                        disabled={!isDriverAssignable(driver) || (Boolean(assigningId) && !isAssigning)}
                    >
                        {t('ASSIGN')}
                    </Button>
                );
            },
        },
    ];

    const fetchAvailableDrivers = () => {
        setLoading(true);
        setErrorMessage(null);

        const query = {
            page: pagination.page,
            limit: pagination.limit,
            driverIds: workspace?.drivers || [],
            bookingID: request?.bookingID,
            search: debouncedSearch || undefined,
        };

        getDrivers(query)
            .then(({ data: response }) => {
                setTotal(response.total);
                setAvailableDrivers(response.data);
            })
            .catch((error: Error) => {
                const description = error.message || 'An error occurred while fetching available drivers.';
                setErrorMessage(description);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setPagination((prev) => (prev.page === PAGE ? prev : { ...prev, page: PAGE }));
    }, [request?._id]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search.trim());
            setPagination((prev) => (prev.page === PAGE ? prev : { ...prev, page: PAGE }));
        }, 700);
        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        if (request?._id) {
            fetchAvailableDrivers();
        }
    }, [request?._id, pagination.page, pagination.limit, debouncedSearch]);

    return (
        <Row gutter={[16, 0]}>
            <Col span={24}>
                <Input
                    allowClear
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t('SEARCH_BY_NAME_EMAIL')}
                    prefix={<IoSearch color="var(--color-dark-2)" />}
                    style={{ marginBottom: 12 }}
                />
                {errorMessage && (
                    <Alert message={errorMessage} type="error" />
                )}
                <Table
                    size="small"
                    columns={columns}
                    dataSource={availableDrivers}
                    pagination={{
                        total,
                        pageSize: pagination.limit,
                        current: pagination.page,
                        onChange: (page, pageSize) => {
                            setPagination((prev) => ({
                                ...prev,
                                page: pageSize !== prev.limit ? PAGE : page,
                                limit: pageSize,
                                total,
                                totalPages: Math.ceil(total / pageSize),
                            }));
                        },
                    }}
                    rowKey="_id"
                    loading={loading}
                    className="table"
                    locale={{
                        emptyText: t('NO_DATA'),
                    }}
                />
            </Col>
        </Row>
    );
};

export { AvailableDrivers };
